import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../api/auth";
import StoreBanner from "../components/store-banner";
import BaseModal from "../components/BaseModal";
import DanceStyleChips from "../components/DanceStyleChips";
import Icon from "@mdi/react";
import { mdiCheckCircleOutline, mdiCheck } from "@mdi/js";
import { useMobileMenu } from "../hooks/useMobileMenu";
import "./RegisterPage.css";
import "./header.css";
import danceLocaleIcon from "../assets/dance_locale_3-200h.png";
import logoImage from "../assets/dance_locale_3-200h.png";
import foxImage from "../assets/Fox Face and Paws.png";

// Shared by the live email check and submit validation.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function RegisterPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    useMobileMenu();

    // Guests arriving from a post-purchase or post-booking "create an account" card
    // carry the address they already typed at checkout (?email=). Seeding it here
    // saves them retyping it and keeps the account tied to the same address the
    // tickets or booking confirmation were sent to. Read once as the initial state
    // so the field stays freely editable afterwards.
    const prefilledEmail = searchParams.get("email") || "";

    // Form state
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState(prefilledEmail);
    const [password, setPassword] = useState("");
    // The person's OWN dance styles — what THEY dance. Required for everyone: this is an
    // individual's account whatever they go on to do with it, it drives event
    // personalisation, and it seeds the org-level style fields at step 2.
    const [danceStyles, setDanceStyles] = useState([]);

    // UI state
    const [error, setError] = useState("");
    const [isRegistering, setIsRegistering] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // Real-time validation state. (No username field — the backend auto-generates
    // a unique username from the email; login is email-only.)
    const [emailCheck, setEmailCheck] = useState({ checking: false, taken: null });
    const emailCheckTimeout = useRef(null);
    // The error box renders above every field, so a failure raised from the bottom of the
    // form (dance styles, password length) would otherwise appear off-screen and the submit
    // would look like it silently did nothing.
    const errorRef = useRef(null);

    // Email existence check with debounce. Only fires once the value passes
    // the same shape test the submit uses (something after the final dot), so
    // a slow typist pausing mid-address does not send a request per pause.
    useEffect(() => {
        const trimmed = email.trim();
        if (!EMAIL_SHAPE.test(trimmed)) {
            setEmailCheck({ checking: false, taken: null });
            return;
        }

        if (emailCheckTimeout.current) clearTimeout(emailCheckTimeout.current);
        setEmailCheck({ checking: true, taken: null });

        emailCheckTimeout.current = setTimeout(async () => {
            try {
                const data = await authApi.checkEmail(trimmed);
                setEmailCheck({ checking: false, taken: data.hasAccount });
            } catch (err) {
                setEmailCheck({ checking: false, taken: null });
            }
        }, 800);

        return () => {
            if (emailCheckTimeout.current) clearTimeout(emailCheckTimeout.current);
        };
    }, [email]);

    // The error renders directly above the submit button, so it is already where the user
    // is looking. Only scroll if it genuinely landed off-screen (very short viewport).
    useEffect(() => {
        if (!error || !errorRef.current) return;
        const r = errorRef.current.getBoundingClientRect();
        if (r.top < 0 || r.bottom > window.innerHeight) {
            errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [error]);

    // Validate Step 1 form (basic account fields only)
    const validateForm = () => {
        if (!firstName.trim() || !lastName.trim()) {
            setError("First name and last name are required");
            return false;
        }
        if (!email || !password) {
            setError("Email and password are required");
            return false;
        }
        if (!EMAIL_SHAPE.test(email.trim())) {
            setError("Please enter a valid email address");
            return false;
        }
        if (password.length < 5) {
            setError("Password must be at least 5 characters");
            return false;
        }
        if (emailCheck.taken) {
            setError("An account with this email already exists. Please log in instead.");
            return false;
        }
        if (danceStyles.length === 0) {
            setError("Please pick at least one dance style");
            return false;
        }
        return true;
    };

    const handleRegister = async e => {
        e.preventDefault();
        setError("");

        if (!validateForm()) {
            return;
        }

        setIsRegistering(true);

        try {
            // Step 1: Register account
            const registerData = await authApi.register({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email,
                password,
                favouriteDanceStyles: danceStyles,
            });

            if (!registerData.success) {
                setError(registerData.message || "Registration failed");
                setIsRegistering(false);
                return;
            }

            // Step 2: Temp login to authenticate for org creation in modal
            const tempLoginData = await authApi.login({ email, password });

            if (!tempLoginData.accessToken) {
                setError("Account created but login failed. Please log in manually.");
                setIsRegistering(false);
                return;
            }

            localStorage.setItem("accessToken", tempLoginData.accessToken);
            localStorage.setItem("refreshToken", tempLoginData.refreshToken);

            // Step 3: account exists and is logged in. Show a success modal (download-app +
            // login details), then continue to the account-type / org-setup page.
            setIsRegistering(false);
            setShowSuccessModal(true);
        } catch (error) {
            console.error("Registration error:", error);
            // The shared client's wrappers throw a plain Error carrying the backend message
            // in error.message; error.response is always undefined via the compat client.
            setError(error.message || "Registration failed. Please try again.");
            setIsRegistering(false);
        }
    };

    // Continue from the success modal to the account-type / org-setup page. Pass the name so
    // the page can pre-fill the instructor teaching name.
    const handleContinueToSetup = () => {
        setShowSuccessModal(false);
        navigate("/register/organisation", {
            state: {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                // Carried so step 2 can prefill its org-level style pickers without a round-trip.
                favouriteDanceStyles: danceStyles,
            },
        });
    };

    return (
        <div className="register-container">
            {/* Header - same as LoginPage */}
            <div data-role="Header" className="home-header-container">
                <header className="home-header">
                    <a href="/" className="home-logo1">
                        <img alt="logo" src={logoImage} className="home-image1" />
                        <span className="nav-wordmark">Dance Localé</span>
                    </a>
                    <div className="home-menu1">
                        <a href="/" className="home-text10" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Home
                        </a>
                        <a href="/login" className="home-text12" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Login
                        </a>
                        <a
                            href="/contact"
                            className="home-text13"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Contact
                        </a>
                    </div>
                    <div className="home-container11">
                        <StoreBanner />
                    </div>
                    <div data-role="BurgerMenu" className="home-burger-menu">
                        <svg viewBox="0 0 1024 1024" className="home-menu2">
                            <path d="M810.667 725.333h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 426.667h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 128h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                        </svg>
                    </div>
                </header>

                {/* Mobile menu structure */}
                <div data-role="MobileMenu" className="home-mobile-menu">
                    <div className="home-top">
                        <div className="home-logo2">
                            <img alt="logo" src={danceLocaleIcon} className="home-image2" />
                        </div>
                        <div data-role="CloseMobileMenu" className="home-close-menu">
                            <svg viewBox="0 0 1024 1024" className="home-icon15">
                                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className="home-mid">
                        <a href="/" className="home-text14" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Home
                        </a>
                        <a href="/login" className="home-text16" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Login
                        </a>
                        <a
                            href="/contact"
                            className="home-text17"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Contact
                        </a>
                    </div>
                </div>
            </div>

            {/* Registration form content */}
            <div className="register-main">
                <div className="register-form-container">
                    <img src={foxImage} alt="Fox Face and Paws" className="register-fox-overlay" />
                    <div className="register-form-card">
                        <div className="register-header">
                            <h1 className="register-title">Create Your Account</h1>
                            <p className="register-subtitle">Join the Dance Localé community</p>
                        </div>

                        {/* noValidate: native HTML5 validation fires BEFORE onSubmit and blocks it entirely,
    so a short password would focus the field with a browser bubble and validateForm()
    would never run — meaning the dance-styles error could never appear. validateForm
    already enforces every rule the required/minLength attributes did, so this makes
    all errors surface the same way, in the error box, which scrolls into view. */}
                        <form onSubmit={handleRegister} className="register-form" noValidate>
                            {/* Basic Account Fields */}
                            <div className="register-field">
                                <label className="register-label" htmlFor="register-first-name">
                                    First Name *
                                </label>
                                <input
                                    type="text"
                                    id="register-first-name"
                                    name="firstName"
                                    autoComplete="given-name"
                                    value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                    className="register-input"
                                    placeholder="Your first name"
                                    required
                                />
                            </div>

                            <div className="register-field">
                                <label className="register-label" htmlFor="register-last-name">
                                    Last Name *
                                </label>
                                <input
                                    type="text"
                                    id="register-last-name"
                                    name="lastName"
                                    autoComplete="family-name"
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    className="register-input"
                                    placeholder="Your last name"
                                    required
                                />
                            </div>

                            <div className="register-field">
                                {/* The email status shares the label's line. Anything rendered
                                    below the input is hidden by the browser's saved-logins
                                    dropdown, and anything inserted above it moves the input while
                                    the dropdown stays anchored to the old position and covers
                                    the field. The label line is always present, so the status
                                    can appear and disappear without shifting anything. */}
                                <div className="register-label-row">
                                    <label className="register-label" htmlFor="register-email">
                                        Email *
                                    </label>
                                    {emailCheck.checking && (
                                        <span className="register-field-hint checking">Checking...</span>
                                    )}
                                    {emailCheck.taken === false && (
                                        <span className="register-field-hint available">
                                            <Icon path={mdiCheck} size={0.6} />
                                            Available
                                        </span>
                                    )}
                                    {emailCheck.taken === true && (
                                        <span className="register-field-hint unavailable">
                                            Email already registered.{" "}
                                            <a
                                                href="/login"
                                                style={{
                                                    color: "var(--dl-color-purple-1000)",
                                                    textDecoration: "underline",
                                                }}>
                                                Log in instead
                                            </a>
                                        </span>
                                    )}
                                </div>
                                <input
                                    type="email"
                                    id="register-email"
                                    name="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className="register-input"
                                    placeholder="your.email@example.com"
                                    required
                                />
                            </div>

                            <div className="register-field">
                                <label className="register-label" htmlFor="register-password">
                                    Password *
                                </label>
                                <input
                                    type="password"
                                    id="register-password"
                                    name="password"
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    className="register-input"
                                    placeholder="Minimum 5 characters"
                                    required
                                    minLength={5}
                                />
                            </div>

                            <div className="register-field">
                                <label className="register-label">Which styles do you dance?</label>
                                <p className="register-field-hint">
                                    Pick any that interest you to help with filtering of instructors, schools and
                                    events. You can change this anytime in Settings.
                                </p>
                                <DanceStyleChips selected={danceStyles} onChange={setDanceStyles} />
                            </div>

                            <p className="register-terms-agreement">
                                By creating an account you agree to our{" "}
                                <a
                                    className="register-login-link-text"
                                    href="/terms-conditions"
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    Terms &amp; Conditions
                                </a>{" "}
                                and{" "}
                                <a
                                    className="register-login-link-text"
                                    href="/privacy-policy"
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    Privacy Policy
                                </a>
                                .
                            </p>

                            {error && (
                                <div className="register-error" ref={errorRef}>
                                    {error}
                                </div>
                            )}

                            <button type="submit" disabled={isRegistering} className="register-button">
                                {isRegistering ? "Creating Account..." : "Create Account"}
                            </button>

                            <p className="register-login-link">
                                Already have an account?{" "}
                                <span
                                    className="register-login-link-text"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate("/login")}
                                    onKeyDown={e => (e.key === "Enter" || e.key === " ") && navigate("/login")}>
                                    Sign in
                                </span>
                            </p>
                        </form>
                    </div>
                </div>
            </div>

            <BaseModal
                isOpen={showSuccessModal}
                onClose={handleContinueToSetup}
                title="Account created"
                icon={mdiCheckCircleOutline}
                footer={
                    <button className="base-modal-save-btn" onClick={handleContinueToSetup}>
                        Continue
                    </button>
                }>
                <p style={{ marginTop: 0, marginBottom: 16, lineHeight: 1.5 }}>
                    Congratulations, your account is ready! You can sign in with the same email on the
                    <strong> Dance Localé mobile app</strong> too:
                </p>
                <p
                    style={{
                        margin: "0 0 20px",
                        fontWeight: 600,
                        fontSize: "1.05rem",
                        color: "var(--dl-color-purple-default)",
                    }}>
                    {email}
                </p>
                <p style={{ margin: 0, color: "var(--dl-color-text-muted)", lineHeight: 1.5 }}>
                    Next, choose how you will use Dance Localé.
                </p>
            </BaseModal>
        </div>
    );
}

export default RegisterPage;
